// The MODULE RECORD-ROUTE REGISTRY — "where does a row of this collection
// open?" (VaultOS v2 design §3.4).
//
// A workspace RECORD (`ref:record:<collection>/<rowId>`) has no shell page.
// Its only home is whatever module page owns its collection, and the shell
// must never learn WHICH module that is — naming one here would make every new
// app a shell change, and would put a tenant's app name in shipped code.
//
// So each installed module declares it, on the `collections` array it already
// uses to declare its owned state keys:
//
//   collections: [{ key: 'vault.items', route: '/vault-rooms', param: 'item' }]
//
// `workspaceProjects.js _normalizeRecordRoutes` validates those rows at load
// (route must be one of the module's own pages, key folded to lower case, a
// half-declaration dropped) and publishes them on
// `root.workspaceProject.recordRoutes` and on each
// `root.workspaceApps[].recordRoutes` — the same two places `opens` rides.
//
// This file is the ONE reader over that pair. Sibling of
// `files/functions/fileHandlers.js liveFileHandlers`, deliberately: same
// union, same order, same reasoning.
//
// ORDER IS THE CONTRACT. The takeover module's rows come first and the
// additive apps' after, so the FIRST declaration of a key wins — an app
// installed from the marketplace can never shadow where the workspace's own
// module opens its records.
//
// ── The one duplicate, and why it is allowed ─────────────────────────────
// `shared/functions/entityRef.js entityRefHref` inlines its own copy of this
// read. It has to: packages must not import from each other (FA001), and
// every function in that file is self-contained on purpose (FA204 — a bare
// sibling read is `undefined` once frank.toJSON has been through it). The two
// stay ONE contract because `__tests__/moduleRecordRoutes.test.js` runs both
// against the same root and requires identical answers. Change one, change
// both, and that test is what says so.

// ── HOW THESE ARE CALLED ──────────────────────────────────────────────────
// Both accept the direct form `(root, …)` AND the `el.call` form, where the
// element is `this` and the root is not passed at all:
//
//   moduleRecordRoute(root, 'vault.items')     // direct (tests, shell code)
//   el.call('moduleRecordRoute', 'vault.items')  // any component
//
// This is not a convenience. `el.call('fn', …args)` runs `fn.call(element,
// …args)` (smbls methods.js), so a component that wrote the obvious call
// passed the COLLECTION KEY into the `root` parameter and got null for every
// lookup — measured live on the worktree shell, 2026-09-09, with a fully
// populated registry sitting right there. A signature that only works when
// the caller knows to thread the root is a signature that will be called
// wrong.
const _recordRoutesRootFrom = (maybeRoot, self) => {
  if (maybeRoot && typeof maybeRoot === 'object') return maybeRoot
  if (!self || typeof self !== 'object') return null
  if (self.state && self.state.root) return self.state.root
  if (typeof self.getRootState === 'function') {
    try {
      return self.getRootState()
    } catch (_) {
      return null
    }
  }
  return null
}

// Every record route live in THIS workspace, as a map keyed by collection.
// Never null — a caller has no empty branch to write.
export function moduleRecordRoutes(maybeRoot) {
  const out = {}
  const root = _recordRoutesRootFrom(maybeRoot, this)
  if (!root || typeof root !== 'object') return out
  const lists = []
  const project = root.workspaceProject
  if (project && typeof project === 'object' && Array.isArray(project.recordRoutes)) {
    lists.push(project.recordRoutes)
  }
  if (Array.isArray(root.workspaceApps)) {
    for (const app of root.workspaceApps) {
      if (app && typeof app === 'object' && Array.isArray(app.recordRoutes)) {
        lists.push(app.recordRoutes)
      }
    }
  }
  for (const rows of lists) {
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue
      const key = row.key == null ? '' : String(row.key).trim().toLowerCase()
      const route = row.route == null ? '' : String(row.route).trim()
      const param = row.param == null ? '' : String(row.param).trim()
      // A half-declared row can never mint a route here either. The loader
      // already dropped these, but a stash written by an older bundle (or a
      // dev override) reaches this function unvalidated.
      if (!key || !route || !param || route.charAt(0) !== '/') continue
      if (out[key]) continue
      out[key] = { route, param }
    }
  }
  return out
}

// `{ route, param }` for ONE collection, or null when no installed module
// claims it. The reader every consumer should use —
// `el.call('moduleRecordRoute', 'vault.items')`.
//
// Case-insensitive on the key for the same reason the loader folds it: the
// ref grammar lower-cases the collection segment, so a caller holding the
// manifest's own spelling and a caller holding a parsed ref must land on the
// same row.
export function moduleRecordRoute(rootOrKey, maybeKey) {
  // `(root, key)` when the first argument is an object, `(key)` otherwise —
  // see the note above `_recordRoutesRootFrom`.
  const rootArg = rootOrKey && typeof rootOrKey === 'object' ? rootOrKey : null
  const rawKey = rootArg ? maybeKey : rootOrKey
  const key = rawKey == null ? '' : String(rawKey).trim().toLowerCase()
  if (!key) return null
  const root = _recordRoutesRootFrom(rootArg, this)
  if (!root) return null
  // Deliberately re-derives the whole map rather than caching: the stash is
  // republished on every workspace activation, and a memo keyed on nothing
  // would serve the previous tenant's routes after a switch.
  const all = moduleRecordRoutes.call(this, root)
  return all[key] || null
}
